import { useContext, useState, useEffect } from 'react';
import { AccountContext } from '../AccountContext';
import { useAccountById, useCurrentBillingPeriod, useGetRecommendedExtraPaymentOptOutOptions, useGetRecommendedExtraPaymentOptOutStatus, useSetRecommendedExtraPaymentOptOutStatus, useSetRecommendedExtraPaymentOptIn, useRefreshOptOutStatus, useOptOutOfCurrentBillingPeriodCharge } from '@app/sdk';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer, Input } from '@app/shared';
import { useCallback } from 'react';
import { Segment, Table, Header } from 'semantic-ui-react';

export default function RecommendedExtraPaymentOptOutOptions() {
  const { accountId, selectedBillingPeriod } = useContext(AccountContext);
  const { data: optOutOptions } = useGetRecommendedExtraPaymentOptOutOptions(accountId);
  const { data: optOutStatus } = useGetRecommendedExtraPaymentOptOutStatus(accountId);

  return (
    <>
    <Segment>
      <Header as="h2">Options</Header>
        <Table celled>
          <Table.Body>
            <Table.Row>
              <Table.Cell>OptOutDisabled</Table.Cell>
              <Table.Cell>{optOutOptions?.optOutDisabled.toString()}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>PermanentOptOutEnabled</Table.Cell>
              <Table.Cell>{optOutOptions?.permanentOptOutEnabled.toString()}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>OptOutBillingPeriodsEnabled</Table.Cell>
              <Table.Cell>{optOutOptions?.optOutBillingPeriodsEnabled.toString()}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>OptOutMaxBillingPeriods</Table.Cell>
              <Table.Cell>{optOutOptions?.optOutMaxBillingPeriods.toString()}</Table.Cell>
            </Table.Row>                        
          </Table.Body>
        </Table>
      </Segment>

      <Segment>
      <Header as="h2">Account Opt-Out Status</Header>
        <Table celled>
          <Table.Body>
            <Table.Row>
              <Table.Cell>OptOutStatus</Table.Cell>
              <Table.Cell>{optOutStatus?.optOutStatus}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>OriginalOptOutBillingPeriods</Table.Cell>
              <Table.Cell>{optOutStatus?.originalOptOutBillingPeriods}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>RemainingOptOutBillingPeriods</Table.Cell>
              <Table.Cell>{optOutStatus?.remainingOptOutBillingPeriods}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>CurrentBillingPeriodOptOut</Table.Cell>
              <Table.Cell>{optOutStatus?.currentBillingPeriodOptOut.toString()}</Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Segment>      

      <UpdateOptOutOptionsForm />
    </>
  );
}

function UpdateOptOutOptionsForm() {
  const { accountId, selectedBillingPeriod } = useContext(AccountContext);
  const refreshOptOutStatus = useRefreshOptOutStatus();
  const { mutate: setRecommendedExtraPaymentOptOutStatus, isPending: isEditPending} = useSetRecommendedExtraPaymentOptOutStatus();
  const { data: optOutStatus } = useGetRecommendedExtraPaymentOptOutStatus(accountId);
  const [billingPeriodId, setBillingPeriodId] = useState<number | undefined>();
  const { data: currentBillingPeriod } = useCurrentBillingPeriod(accountId);
  const { data: account } = useAccountById(accountId);
  const { mutate: setRecommendedExtraPaymentOptIn } = useSetRecommendedExtraPaymentOptIn();
  const { mutate: optOutCurrentBillingPeriodCharge } = useOptOutOfCurrentBillingPeriodCharge();

  useEffect(() => {
    setBillingPeriodId(selectedBillingPeriod.billingPeriodId);
    setTimeout(() => {
        refreshOptOutStatus();
    }, 10);
}, [currentBillingPeriod?.billingPeriodId, selectedBillingPeriod, refreshOptOutStatus, account]);

  const submitBillingPeriodsOptOut = useCallback(async (data) => {
    await setRecommendedExtraPaymentOptOutStatus({
        accountId,
        permanentOptOut: null,
        optOutBillingPeriods: data.optOutBillingPeriods,
      });
      setTimeout(() => {
        refreshOptOutStatus();
      }, 2000);      
    },
    [accountId, setRecommendedExtraPaymentOptOutStatus]
  );

  const submitOptOutOfCurrentBillingPeriodCharge = useCallback(async (data) => {
      await optOutCurrentBillingPeriodCharge({accountId});

      setTimeout(() => {
        refreshOptOutStatus();
      }, 2000);
    },
    [accountId, optOutCurrentBillingPeriodCharge]
  );

  const submitPermanentOptOutTrue = useCallback(async (data) => {
      await setRecommendedExtraPaymentOptOutStatus({
        accountId,
        permanentOptOut: true,
        optOutBillingPeriods: null,
      });
      setTimeout(() => {
        refreshOptOutStatus();
      }, 2000);
    },
    [accountId, setRecommendedExtraPaymentOptOutStatus]
  );

  const submitOptIn = useCallback(async (data) => {
   await setRecommendedExtraPaymentOptIn({accountId});
    setTimeout(() => {
      refreshOptOutStatus();
    }, 2000);
  },
  [accountId, setRecommendedExtraPaymentOptIn]
);

  const formContext = useForm<{optOutBillingPeriods: number, permanentOptOut: boolean}>();

  return (
    <div>
      <Segment>
      <Header as="h2">Opt In for Recommended Extra Payment</Header>
        <button 
          type="button"
          className="ui button blue"
          onClick={submitOptIn}>
          <span>Opt In</span>
        </button>     
      </Segment>
      <Segment>
      <Header as="h2">Opt Out for X Billing Periods</Header>
        <FormProvider {...formContext}>
          <FormContainer 
            isSubmitting={isEditPending}
            onSubmit={submitBillingPeriodsOptOut}>          
            <Input
              name={'optOutBillingPeriods'}
              label={'Enter Number of billing periods'}
              defaultValue={''}
            />
          </FormContainer>
        </FormProvider>
      </Segment>
      <Segment>
      <Header as="h2">Opt Out</Header>
        <button 
          type="button"
          className="ui button blue"
          onClick={submitOptOutOfCurrentBillingPeriodCharge}>
          <span>OptOut of Current Billing Period Charge</span>
        </button>     

        <button 
          type="button"
          className="ui button blue"
          onClick={submitPermanentOptOutTrue}>
          <span>OptOut Permanently</span>
        </button>     
      </Segment>
    </div>
  );
}